import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import { SecondaryButton, DangerButton } from '../ui';
import type { FinanceInsight, InsightEvidence } from '../../api/financeApi';

interface Props {
  insight: FinanceInsight | null;
  onClose: () => void;
  onAct: (insight: FinanceInsight) => void;
  onDismiss: (id: number) => void;
  onSnooze: (id: number, days: number) => void;
  onHideType: (type: string) => void;
}

const sevColor = (sev: string): string => {
  if (sev === 'CRITICAL') return '#D32F2F';
  if (sev === 'WARN') return '#ED6C02';
  return '#0288D1';
};

const labelize = (key: string): string =>
  key
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');

const fmtValue = (key: string, v: unknown): string => {
  if (v == null) return '—';
  if (typeof v === 'number') {
    const lower = key.toLowerCase();
    const isMoney =
      lower.includes('amount') ||
      lower.includes('total') ||
      lower.includes('paid') ||
      lower.includes('agreed') ||
      lower.includes('salary') ||
      lower.includes('income') ||
      lower.includes('variance') ||
      lower.includes('over') ||
      lower.endsWith('_a') ||
      lower.endsWith('_b') ||
      lower.includes('diff');
    const isPct = lower.includes('pct') || lower.includes('ratio_pct');
    if (isPct) return `${v.toFixed(2)}%`;
    if (isMoney) return formatCurrency(v);
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toFixed(2);
  }
  return String(v);
};

const MathBlock: React.FC<{ data: Record<string, unknown>; thresholds?: Record<string, unknown> }> = ({
  data,
  thresholds,
}) => (
  <Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
        rowGap: 0.5,
        columnGap: 2,
      }}
    >
      {Object.entries(data).map(([k, v]) => (
        <React.Fragment key={k}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {labelize(k)}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {fmtValue(k, v)}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
    {thresholds && Object.keys(thresholds).length > 0 && (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Triggers when:
        </Typography>
        <Box sx={{ pl: 1.5 }}>
          {Object.entries(thresholds).map(([k, v]) => (
            <Typography key={k} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
              · {labelize(k)} = {fmtValue(k, v)}
            </Typography>
          ))}
        </Box>
      </Box>
    )}
  </Box>
);

const RowsTable: React.FC<{ rows: Array<Record<string, unknown>> }> = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  const cols = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {cols.map((c) => (
              <TableCell key={c} sx={{ fontWeight: 700, fontSize: '0.74rem' }}>
                {labelize(c)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {cols.map((c) => (
                <TableCell key={c} sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>
                  {fmtValue(c, r[c])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', mb: 0.75 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const InsightDrawer: React.FC<Props> = ({ insight, onClose, onAct, onDismiss, onSnooze, onHideType }) => {
  const { theme } = useTheme();
  const open = Boolean(insight);
  const ev: InsightEvidence | null = insight?.evidence ?? null;
  const sev = insight?.severity || 'INFO';
  const color = sevColor(sev);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 520 }, maxWidth: '100vw' } }}
    >
      {insight && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.gray?.[200] || '#EEE'}`, borderTop: `4px solid ${color}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip
                    size="small"
                    label={sev}
                    sx={{ background: `${color}1F`, color, fontWeight: 700, height: 20 }}
                  />
                  {insight.impactAmount != null && (
                    <Chip
                      size="small"
                      label={`${insight.impactAmount < 0 ? '-' : ''}${formatCurrency(
                        Math.abs(insight.impactAmount),
                      )}${
                        insight.impactPeriod === 'WEEKLY'
                          ? '/wk'
                          : insight.impactPeriod === 'MONTHLY'
                          ? '/mo'
                          : ''
                      }`}
                      sx={{ background: 'var(--bg-sunken)', fontWeight: 700, height: 20 }}
                    />
                  )}
                  {insight.confidence != null && (
                    <Chip
                      size="small"
                      label={`${Math.round(insight.confidence * 100)}% confidence`}
                      sx={{ background: 'var(--bg-sunken)', height: 20 }}
                    />
                  )}
                </Stack>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3 }}>
                  {insight.title}
                </Typography>
              </Box>
              <IconButton onClick={onClose} size="small">
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </IconButton>
            </Stack>
          </Box>

          {/* Body */}
          <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
            <Stack spacing={2}>
              <Section title="What we noticed">
                <Typography variant="body2">{insight.body}</Typography>
              </Section>

              {ev?.math && (
                <>
                  <Divider />
                  <Section title="The math">
                    <MathBlock data={ev.math} thresholds={ev.thresholds} />
                  </Section>
                </>
              )}

              {ev?.rows && ev.rows.length > 0 && (
                <>
                  <Divider />
                  <Section title="Where the numbers come from">
                    <RowsTable rows={ev.rows} />
                  </Section>
                </>
              )}

              {ev?.suggestions && ev.suggestions.length > 0 && (
                <>
                  <Divider />
                  <Section title="What you can do">
                    <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                      {ev.suggestions.map((s, i) => (
                        <Typography component="li" key={i} variant="body2" sx={{ mb: 0.5 }}>
                          {s}
                        </Typography>
                      ))}
                    </Box>
                  </Section>
                </>
              )}

              <Divider />
              <Section title="Detection details">
                <Stack spacing={0.25}>
                  <Typography variant="caption">
                    Detector: <code>{ev?.detector || insight.insightType}</code>
                  </Typography>
                  <Typography variant="caption">
                    Detected at: {new Date(insight.detectedAt).toLocaleString()}
                  </Typography>
                  {insight.periodStart && insight.periodEnd && (
                    <Typography variant="caption">
                      Period: {insight.periodStart} → {insight.periodEnd}
                    </Typography>
                  )}
                </Stack>
              </Section>
            </Stack>
          </Box>

          {/* Footer */}
          <Box sx={{ p: 2, borderTop: `1px solid ${theme.gray?.[200] || '#EEE'}` }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Button
                variant="contained"
                onClick={() => onAct(insight)}
                sx={{
                  background: color,
                  minHeight: 44,
                  '&:hover': { background: color, opacity: 0.9 },
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                }}
              >
                {insight.actionType ? labelize(insight.actionType.toLowerCase()) : 'Open'}
              </Button>
              <Stack direction="row" spacing={1}>
                <SecondaryButton onClick={() => onSnooze(insight.id, 7)} size="small">
                  Snooze 7d
                </SecondaryButton>
                <SecondaryButton onClick={() => onDismiss(insight.id)} size="small">
                  Dismiss
                </SecondaryButton>
                <DangerButton
                  outlined
                  onClick={() => onHideType(insight.insightType)}
                  size="small"
                >
                  Hide type
                </DangerButton>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default InsightDrawer;
